import React from 'react';
import { AnimalType, AnimalGender } from '../../models/animal';
import { extractApiErrorMessage, notifications } from '../../utils';
import { AnimalTypeSelect, AnimalGenderSelect, FarmLocationSelectModal } from '../../elements';
import { useStyles } from './styles';
import {
  Icon,
  Dialog,
  Button,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  DialogActions
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { ApiService } from '../../hooks';
import {
  HealthSystemDomainAnimalsCommandsUpdateAnimalCommand,
  HealthSystemDomainDiseasesDiseaseViewModel
} from '../../api/HealthSystemApi';
import { LocationOption, toLocationOption } from '../../models/locations';
import { MortalityReasonOption, toMortalityReasonOption } from '../../models/injection';
import FlexiblePropertiesForm, { FlexiblePropertiesFormRef } from '../FlexiblePropertiesForm';
import AnimalDieReasonSelect from '../../elements/AnimalDieReasonSelect';
import { buildInitialMetadata, buildMetadataFromEntries } from './utils';

interface IProps {
  animalId: string;
  onAnimalUpdated?: () => void;
}

const AnimalUpdateModal = ({ animalId, onAnimalUpdated }: IProps) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const apiService = new ApiService();

  const [animal, setAnimal] = React.useState<HealthSystemDomainAnimalsCommandsUpdateAnimalCommand | null>(null);
  const [isBusy, setIsBusy] = React.useState(false);

  const [gender, setGender] = React.useState(0);
  const [animalType, setAnimalType] = React.useState(0);

  const [loadingDiseases, setLoadingDiseases] = React.useState<boolean>(false);
  const [loadingMortalityReasons, setLoadingMortalityReasons] = React.useState<boolean>(false);
  const [diseases, setDiseases] = React.useState<HealthSystemDomainDiseasesDiseaseViewModel[]>([]);
  const [mortalityReasons, setMortalityReasons] = React.useState<MortalityReasonOption[]>([]);
  const [isDied, setIsDied] = React.useState<boolean | undefined>(false);
  const [, setDiseaseId] = React.useState<string>('');
  const [, setMortalityReasonId] = React.useState<string>('');
  const [location, setLocation] = React.useState<LocationOption | null>(null);

  const [dialogOpen, setDialogOpen] = React.useState(false);

  const openUpdateAnimalDialog = () => {
    setDialogOpen(true);
    loadFarmDiseases();
    loadMortalityReasons();
    loadAnimalData();
  };

  const closeUpdateAnimalDialog = (text: string | null) => {
    if (text == null) {
      setDialogOpen(false);
      return;
    }

    updateAnimal();
  };

  const loadAnimalData = () => {
    setIsBusy(true);

    apiService.api.animals
      .animalsGetAnimalById({ Id: animalId })
      .then((result) => {
        setAnimal(result.data);
        setGender(result.data.gender);
        setLocation(result.data.location ? toLocationOption(result.data.location) : null);
        setAnimalType(result.data.animalType);
        setIsDied(result.data.isDied);
        setDiseaseId(result.data.diseaseId || '');
        setMortalityReasonId(result.data.mortalityReasonId || '');
        setIsBusy(false);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      });
  };

  // todo: Extract into IAnimalValidator
  const validateUpdate = (): boolean => {
    if (!animal) {
      return false;
    }

    //gender + animal type
    if (animal.animalType === AnimalType.Gilt || animal.animalType === AnimalType.Sow) {
      if (animal.gender !== AnimalGender.Female) {
        notifications.warning(t('f-animal-upd-femtype'));
        return false;
      }
    }

    if (animal.isDied) {
      const hasDisease = !!animal.diseaseId && animal.diseaseId.trim() !== '';
      const hasMortality = !!animal.mortalityReasonId && animal.mortalityReasonId.trim() !== '';

      if (!hasDisease && !hasMortality) {
        notifications.warning(t('f-animal-upd-disease-or-mortality'));
        return false;
      }

      if (hasDisease && hasMortality) {
        notifications.warning(t('f-animal-upd-disease-and-mortality'));
        return false;
      }
    }

    return true;
  };

  const updateAnimal = async () => {
    if (!animal) {
      return;
    }

    const valid = validateUpdate();
    if (!valid) {
      return;
    }

    if (!animal.isDied) {
      animal.diseaseId = '';
      animal.mortalityReasonId = '';
    }

    apiService.api.animals
      .animalsUpdateAnimal(buildUpdateAnimalCommand())
      .then(() => {
        notifications.success(t('f-animal-upd-ok'));
        setDialogOpen(false);
        if (onAnimalUpdated) {
          onAnimalUpdated();
        }
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      });
  };

  const dynamicFormRef = React.useRef<FlexiblePropertiesFormRef>(null);

  const buildUpdateAnimalCommand = (): HealthSystemDomainAnimalsCommandsUpdateAnimalCommand => {
    if (!animal) {
      throw new Error('Animal must be loaded before building the update command');
    }

    // Get the latest dynamic entries from the ref.
    const dynamicEntries = dynamicFormRef.current?.getEntries() || [];
    const updatedMetadata = buildMetadataFromEntries(animal.metadata, dynamicEntries);

    return {
      id: animal.id,
      farmId: animal.farmId,
      animalType: animal.animalType,
      genetics: animal.genetics,
      gender: animal.gender,
      isDied: animal.isDied,
      isSoftDeleted: animal.isSoftDeleted,
      diseaseId: animal.diseaseId,
      mortalityReasonId: animal.mortalityReasonId,
      locationId: animal.locationId,
      employeeRegisteredId: animal.employeeRegisteredId,
      nipples: animal.nipples,
      weight: animal.weight,
      metadata: updatedMetadata
    };
  };

  const loadMortalityReasons = async () => {
    setLoadingMortalityReasons(true);

    apiService.api.mortalityReasons
      .mortalityReasonsMortalityReasonsSearch({})
      .then((result) => {
        setMortalityReasons(result.data.data.map(toMortalityReasonOption));
        setLoadingMortalityReasons(false);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setLoadingMortalityReasons(false);
      });
  };

  const loadFarmDiseases = async () => {
    setLoadingDiseases(true);

    apiService.api.diseases
      .diseasesGetAllDiseases({})
      .then((result) => {
        setDiseases(result.data);
        setLoadingDiseases(false);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setLoadingDiseases(false);
      });
  };
  interface DieReasons {
    id: string;
    name: string;
  }

  const mapFromDiseasesToDieReasons = (items: HealthSystemDomainDiseasesDiseaseViewModel[]): DieReasons[] => {
    return items.map((item) => ({
      id: item.id,
      name: item.name ?? '-'
    }));
  };

  return (
    <>
      <Button
        id="updateAnimalBtn"
        size="small"
        color="default"
        variant="contained"
        startIcon={<Icon>{'edit'}</Icon>}
        onClick={openUpdateAnimalDialog}
      >
        {t('f-com-edit')}
      </Button>

      <Dialog
        id="updateAnimalDialog"
        open={dialogOpen}
        onClose={() => closeUpdateAnimalDialog(null)}
        className={classes.dialog}
        maxWidth="sm"
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="updateAnimalDialogTitle" className={classes.dialogHeaderFooter}>
          {t('f-animal-upd-title')}
        </DialogTitle>

        <DialogContent>
          {isBusy === true && (
            <>
              <div className={classes.spinnerBox}>
                <CircularProgress size={72} />
              </div>
            </>
          )}

          {isBusy === false && animal && (
            <Grid container direction="column" spacing={2} className={classes.updateForm}>
              <form
                noValidate
                autoComplete="off"
                onSubmit={(e) => {
                  e.preventDefault();
                  updateAnimal();
                }}
              >
                <Grid item>
                  <AnimalTypeSelect
                    value={animalType}
                    cssClass={classes.formControl}
                    allowAny={false}
                    onAnimalTypeChange={(type) => {
                      setAnimalType(type);
                      animal.animalType = type;
                    }}
                  />
                </Grid>

                <Grid item>
                  <AnimalGenderSelect
                    value={gender}
                    allowAny={false}
                    cssClass={classes.formControl}
                    onAnimalGenderChange={(value) => {
                      setGender(value);
                      animal.gender = value;
                    }}
                  />
                </Grid>

                <Grid item>
                  <TextField
                    id="gen-input"
                    label={t('f-genetics')}
                    variant="outlined"
                    multiline
                    className={classes.formControl}
                    defaultValue={animal.genetics}
                    size="small"
                    onChange={(e) => {
                      animal.genetics = e.target.value;
                    }}
                  />
                </Grid>

                <Grid item>
                  <FarmLocationSelectModal
                    disableAny={false}
                    location={location}
                    cssClass={classes.formControl}
                    onChange={(location) => {
                      setLocation(location);
                      animal.locationId = location?.id;
                    }}
                  />
                </Grid>

                <Grid item>
                  <FormControlLabel
                    className={classes.formControl}
                    control={
                      <Checkbox
                        checkedIcon={<Icon>{'cancel'}</Icon>}
                        defaultChecked={animal.isDied}
                        onChange={(e) => {
                          setIsDied(e.target.checked);

                          if (!e.target.checked) {
                            setDiseaseId('');
                          }

                          animal.isDied = e.target.checked;
                        }}
                      />
                    }
                    label={t('f-animal-view-died')}
                  />
                </Grid>

                {isDied && (
                  <Grid container spacing={1} direction="column">
                    <Grid item>
                      <AnimalDieReasonSelect
                        fullWidth
                        disabled={loadingDiseases}
                        options={mapFromDiseasesToDieReasons(diseases)}
                        value={animal.diseaseId || ''}
                        onChange={(newValue) => {
                          animal.diseaseId = newValue;
                          setDiseaseId(newValue);
                        }}
                        labelKey="f-disease"
                        selectId="AnimalDiseaseSelect"
                      />
                    </Grid>

                    <Grid item>
                      <AnimalDieReasonSelect
                        fullWidth
                        disabled={loadingMortalityReasons}
                        options={mortalityReasons}
                        value={animal.mortalityReasonId || ''}
                        onChange={(newValue) => {
                          animal.mortalityReasonId = newValue;
                          setMortalityReasonId(newValue);
                        }}
                        labelKey="f-mortality-reason"
                        selectId="AnimalMortalityReasonSelect"
                      />
                    </Grid>
                  </Grid>
                )}
                <Grid item>
                  <FlexiblePropertiesForm
                    ref={dynamicFormRef}
                    initialEntries={buildInitialMetadata(animal.metadata)}
                    hideConfirmButton
                  />
                </Grid>

                <Grid item>
                  <FormControlLabel
                    className={classes.formControl}
                    control={
                      <Checkbox
                        checkedIcon={<Icon>{'delete'}</Icon>}
                        defaultChecked={animal.isSoftDeleted}
                        onChange={(e) => {
                          animal.isSoftDeleted = e.target.checked;
                        }}
                      />
                    }
                    label={t('f-animal-view-deleted')}
                  />
                </Grid>
              </form>
            </Grid>
          )}
        </DialogContent>

        <DialogActions className={classes.dialogHeaderFooter}>
          <Button
            onClick={() => closeUpdateAnimalDialog(null)}
            variant="outlined"
            startIcon={<Icon>{'close'}</Icon>}
            color="primary"
          >
            {t('f-com-cancel')}
          </Button>

          <Button
            onClick={() => closeUpdateAnimalDialog('OK')}
            startIcon={<Icon>{'save'}</Icon>}
            variant="contained"
            color="primary"
          >
            {t('f-com-save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AnimalUpdateModal;
